import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Managers' queue of staff who have said when they're coming back.
// CJ 2026-08-04, D229.
//
// Accepting is ONE action on purpose: it sets status active, clears last_day,
// and records the leave_end on the employment timeline together. Splitting
// those is what produced the trap where a restored person looked active but
// stayed invisible to the schedule, because scheduling reads last_day rather
// than status.
//
// Renders nothing at all when the queue is empty — a permanently-present
// "0 pending" card is noise on a page managers use for something else.

const C = {
  ink: '#1c1f1d', inkSoft: '#4a4d4a', faint: '#7a7d79',
  card: '#fff', line: '#d8d2c2', forest: '#2f5d50',
  gold: '#b48a47', goldBg: '#fdf6e9', crimson: '#8a3a3a',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pretty(ymd) {
  if (!ymd) return '';
  const d = new Date(String(ymd).slice(0, 10) + 'T12:00:00Z');
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function whenLabel(days) {
  if (days == null) return '';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 0) return `${Math.abs(days)} days ago`;
  return `in ${days} days`;
}

export function LeaveReturnsPanel({ currentUser, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');
  const headers = { 'x-user-id': String(currentUser.id) };

  async function load() {
    try {
      const r = await api('GET', '/api/admin/leave-returns', null, headers);
      setRows(r.returns || []);
    } catch (e) { /* a manager without the capability just sees nothing */ }
  }
  useEffect(() => { load(); }, []);

  async function accept(row) {
    setBusyId(row.id); setErr('');
    try {
      await api('PUT', `/api/admin/leave-returns/${row.id}/accept`, {}, headers);
      await load();
      onChanged?.();
    } catch (e) {
      let m = e?.message || 'Could not bring them back';
      try { m = JSON.parse(m).error || m; } catch { /* plain */ }
      setErr(m);
    } finally { setBusyId(null); }
  }

  async function decline(row) {
    const note = prompt(`Why is ${row.name}'s return date not right? They'll see this.`);
    if (note == null) return;
    if (!note.trim()) { setErr('A reason is required to decline.'); return; }
    setBusyId(row.id); setErr('');
    try {
      await api('PUT', `/api/admin/leave-returns/${row.id}/decline`,
        { manager_note: note.trim() }, headers);
      await load();
    } catch (e) {
      let m = e?.message || 'Could not decline';
      try { m = JSON.parse(m).error || m; } catch { /* plain */ }
      setErr(m);
    } finally { setBusyId(null); }
  }

  if (!rows.length) return null;

  return (
    <div style={{ background: C.goldBg, border: `1px solid ${C.gold}`,
                  borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
        {rows.length} {rows.length === 1 ? 'person is' : 'people are'} coming back
      </div>
      <div style={{ fontSize: 12, color: C.inkSoft, margin: '2px 0 10px' }}>
        They sent this themselves while on leave. Bringing them back switches their
        account on, puts them on the schedule and clears their last day — all in one go.
      </div>
      {err && <div style={{ fontSize: 12, color: C.crimson, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r) => (
          <div key={r.id} style={{ background: C.card, border: `1px solid ${C.line}`,
                                    borderRadius: 8, padding: '10px 12px',
                                    display: 'flex', gap: 12, flexWrap: 'wrap',
                                    alignItems: 'center' }}>
            <div style={{ flex: '1 1 220px', minWidth: 200 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {r.name}
                <span style={{ fontWeight: 400, color: C.faint, fontSize: 11 }}>
                  {' '}· {r.role} · {r.cafe}
                </span>
              </div>
              <div style={{ fontSize: 12, color: C.ink, marginTop: 2 }}>
                Back <strong>{pretty(r.return_date)}</strong>
                <span style={{ color: C.faint }}> ({whenLabel(r.days_until)})</span>
              </div>
              {r.availability_note && (
                <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 2 }}>
                  “{r.availability_note}”
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => accept(r)} disabled={busyId === r.id}
                style={{ background: C.forest, color: '#fff', border: 'none',
                          borderRadius: 6, padding: '6px 14px', fontSize: 12,
                          fontWeight: 600, fontFamily: 'inherit',
                          cursor: busyId === r.id ? 'not-allowed' : 'pointer',
                          opacity: busyId === r.id ? 0.5 : 1 }}>
                {busyId === r.id ? '…' : 'Bring back'}
              </button>
              <button onClick={() => decline(r)} disabled={busyId === r.id}
                style={{ background: '#fff', color: C.crimson,
                          border: `1px solid ${C.crimson}44`, borderRadius: 6,
                          padding: '6px 12px', fontSize: 12, fontWeight: 600,
                          fontFamily: 'inherit', cursor: 'pointer' }}>
                Not yet
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
